import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Star, Building2, Pencil, MapPin, Lock, Loader2, RefreshCw } from "lucide-react";
import { PositionMap } from "@/components/EstablishmentPicker";

const TYPES = [["hopital", "Hôpital"], ["chu", "CHU"], ["clinique", "Clinique"], ["hopital_specialise", "Hôpital spécialisé"], ["readaptation", "Rééducation / SSR"], ["maison_de_sante", "Maison de santé / MSP"], ["cabinet_medical", "Cabinet médical"], ["cabinet_kine", "Cabinet de kinésithérapie"], ["autre_pro_sante", "Autre professionnel de santé"], ["ehpad", "EHPAD"], ["cabinet", "Cabinet / centre"], ["personnalise", "Autre"]];
const typeLabel = (t) => TYPES.find(([k]) => k === t)?.[1] || "Autre";
const empty = { name: "", type: "personnalise", address: "", postal_code: "", city: "", lat: null, lng: null };

export default function Establishments() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null); // null | { id?, ...fields }

  const load = () => api.get("/establishments").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggleFav = async (e) => { await api.post(`/establishments/${e.id}/favorite`); load(); };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-3xl font-heading font-extrabold text-brand-900">Établissements</h1><p className="text-slate-500 mt-1">Base commune SanitDocs et établissements personnalisés de votre organisation. L'étoile est votre favori personnel.</p></div>
        <Button data-testid="create-establishment-button" onClick={() => setEdit({ ...empty })} className="bg-accent-600 hover:bg-accent-700 h-11"><Plus className="w-4 h-4 mr-1.5" />Nouvel établissement</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((e) => (
          <div key={e.id} data-testid={`establishment-${e.id}`} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-accent-50 text-accent-700 flex items-center justify-center shrink-0"><Building2 className="w-5 h-5" /></div>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-brand-900 leading-tight">{e.name}</p>
              <p className="text-xs text-slate-500 mt-0.5 truncate">{[e.address, e.postal_code, e.city].filter(Boolean).join(", ") || "Adresse non renseignée"}</p>
              <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1 flex-wrap">
                <span>{typeLabel(e.type)}</span>
                {e.facility_id ? <span className="inline-flex items-center gap-0.5 text-accent-700"><Lock className="w-3 h-3" />Base commune</span> : <span>Personnalisé</span>}
                {e.lat != null ? <span className="inline-flex items-center gap-0.5 text-green-700"><MapPin className="w-3 h-3" />{e.geo_source === "manual" ? "Position corrigée" : "Géolocalisé"}</span> : <span className="text-orange-600">Sans position</span>}
              </p>
            </div>
            <div className="flex flex-col items-center gap-2 shrink-0">
              <button data-testid={`toggle-fav-${e.id}`} onClick={() => toggleFav(e)} aria-label="Favori"><Star className={`w-6 h-6 ${e.favorite ? "text-orange-400 fill-orange-400" : "text-slate-300"}`} /></button>
              {!e.facility_id && <button data-testid={`edit-est-${e.id}`} onClick={() => setEdit({ ...empty, ...e })} aria-label="Modifier" className="text-slate-400 hover:text-brand-800"><Pencil className="w-5 h-5" /></button>}
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-slate-400 col-span-full text-center py-10">Aucun établissement.</p>}
      </div>

      {edit && <EditDialog initial={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function EditDialog({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [sugg, setSugg] = useState([]);
  const isEdit = !!initial.id;
  const addrChanged = isEdit && ["address", "postal_code", "city"].some((k) => (f[k] || "") !== (initial[k] || ""));

  const suggest = (v) => {
    setF({ ...f, address: v });
    if (v.length < 3) return setSugg([]);
    api.get("/geocode/suggest", { params: { q: v } }).then((r) => setSugg(r.data)).catch(() => setSugg([]));
  };
  const save = async (regeocode = false) => {
    if (!f.name.trim()) return toast.error("Nom requis");
    setBusy(true);
    const body = { name: f.name, type: f.type, address: f.address, postal_code: f.postal_code, city: f.city, regeocode,
      lat: regeocode || addrChanged ? null : f.lat, lng: regeocode || addrChanged ? null : f.lng };
    try {
      const { data } = isEdit ? await api.patch(`/establishments/v2/${f.id}`, body) : await api.post("/establishments/v2", body);
      if (data.geo_failed) toast.warning("Adresse non géolocalisée. Vérifiez l'adresse ou placez le repère manuellement.");
      else toast.success(isEdit ? "Établissement mis à jour" : "Établissement créé");
      if (regeocode) { setF({ ...f, lat: data.lat, lng: data.lng, geo_source: data.geo_source }); }
      else onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="establishment-dialog" className="max-w-md w-[calc(100vw-24px)] max-h-[92vh] overflow-y-auto rounded-2xl">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Modifier l'établissement" : "Nouvel établissement personnalisé"}</DialogTitle>
          <DialogDescription>Établissement propre à votre organisation. La position corrigée sur la carte est prioritaire.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nom</Label><Input data-testid="establishment-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 h-11" placeholder="Ex : Cabinet de radiologie" /></div>
          <div><Label>Type</Label>
            <select data-testid="establishment-type" value={f.type || "personnalise"} onChange={(e) => setF({ ...f, type: e.target.value })} className="mt-1 h-11 w-full rounded-lg border border-slate-200 px-3 bg-white">
              {TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></div>
          <div className="relative"><Label>Adresse</Label><Input data-testid="establishment-address" value={f.address || ""} onChange={(e) => suggest(e.target.value)} className="mt-1 h-11" placeholder="Numéro, rue" />
            {sugg.length > 0 && <div className="absolute z-20 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg divide-y divide-slate-100 mt-1">{sugg.map((s, i) => (
              <button type="button" key={i} data-testid={`addr-sugg-${i}`} onClick={() => { setF({ ...f, address: s.address || s.label, postal_code: s.postal_code || "", city: s.city || "", lat: s.lat, lng: s.lng }); setSugg([]); }} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50">{s.label}</button>))}</div>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Code postal</Label><Input data-testid="establishment-cp" value={f.postal_code || ""} onChange={(e) => setF({ ...f, postal_code: e.target.value })} className="mt-1 h-11" /></div>
            <div><Label>Commune</Label><Input data-testid="establishment-city" value={f.city || ""} onChange={(e) => setF({ ...f, city: e.target.value })} className="mt-1 h-11" /></div>
          </div>
          {addrChanged && <p data-testid="addr-changed-hint" className="text-xs text-orange-600">Adresse modifiée : la position sera recalculée automatiquement à l'enregistrement.</p>}
          {f.lat != null ? (
            <>
              <p data-testid="establishment-coords" className="text-xs text-slate-500">Position : {Number(f.lat).toFixed(5)}, {Number(f.lng).toFixed(5)} — déplacez le repère pour corriger.</p>
              <PositionMap lat={f.lat} lng={f.lng} onMove={(lat, lng) => setF((p) => ({ ...p, lat, lng, geo_source: "manual" }))} className="h-56" />
            </>
          ) : isEdit && <p className="text-xs text-orange-600">Aucune position connue. Renseignez l'adresse puis relancez le géocodage.</p>}
          {isEdit && (f.address || f.city) && (
            <button type="button" data-testid="regeocode-btn" onClick={() => save(true)} disabled={busy} className="text-sm text-accent-700 font-semibold inline-flex items-center gap-1"><RefreshCw className="w-4 h-4" />Relancer le géocodage depuis l'adresse</button>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button data-testid="submit-establishment" onClick={() => save(false)} disabled={busy} className="bg-accent-600 hover:bg-accent-700">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{isEdit ? "Enregistrer" : "Créer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
